import type {
  MonthlyContractRow,
  MonthlyEditableRowValues,
  MonthlyExistingUtilityRow,
  MonthlyRoomRow,
  MonthlyRowEntry,
  MonthlyUtilityRow,
  SnapshotDetailRow,
} from './interfaces/MonthlyRecord'

type PrevMonthUtilityRecord = Pick<
  MonthlyUtilityRow,
  'room_number' | 'elec_curr_reading' | 'water_curr_reading' | 'record_date'
>

type LiveMonthlyRowsInput = {
  month: string
  rooms: MonthlyRoomRow[]
  contracts: MonthlyContractRow[]
  existingRecords: MonthlyUtilityRow[]
  prevMonthRecords: PrevMonthUtilityRecord[]
}

type SnapshotMonthlyRowsInput = {
  snapshotRows: SnapshotDetailRow[]
  rooms: MonthlyRoomRow[]
  contracts: MonthlyContractRow[]
  existingRecords: MonthlyUtilityRow[]
}

export const ROOM_SYNC_FIELDS = [
  'price_monthly',
  'internet_fee',
  'furniture_fee',
  'maintenance_fee',
  'other_fee',
  'other_fee_label',
  'room_type',
  'room_status',
  'room_remark',
  'repair_details',
  'keycards',
] as const

export type RoomSyncField = (typeof ROOM_SYNC_FIELDS)[number]

export const SNAPSHOT_COMPARABLE_FIELDS = [
  'price_monthly',
  'elec_unit_price',
  'elec_prev_reading',
  'elec_curr_reading',
  'water_unit_price',
  'water_prev_reading',
  'water_curr_reading',
  'internet_fee',
  'furniture_fee',
  'maintenance_fee',
  'other_fee',
  'other_fee_label',
  'record_date',
  'payment_status',
  'remark',
  'room_type',
  'room_status',
  'room_remark',
  'repair_details',
  'keycards',
] as const

export type SnapshotComparableField =
  (typeof SNAPSHOT_COMPARABLE_FIELDS)[number]
type StringSnapshotComparableField = Exclude<
  SnapshotComparableField,
  'payment_status'
>

const tenantNameFromContract = (
  contract: MonthlyContractRow | null,
): string | null => {
  if (!contract?.tenant) return null
  return (
    `${contract.tenant.first_name ?? ''} ${
      contract.tenant.last_name ?? ''
    }`.trim() || null
  )
}

const emptyForm = (
  room: {
    room_type: string
    status: string
    elec_unit_price: number
    water_unit_price: number
    price_monthly: number
    internet_fee: number
    furniture_fee: number
    maintenance_fee: number
    other_fee: number
    other_fee_label: string | null
    remark: string | null
    repair_details: string | null
    keycards: string[] | null
  },
  recordDate: string,
): MonthlyEditableRowValues => ({
  elec_unit_price: String(room.elec_unit_price),
  elec_prev_reading: '',
  elec_curr_reading: '',
  water_unit_price: String(room.water_unit_price),
  water_prev_reading: '',
  water_curr_reading: '',
  record_date: recordDate,
  payment_status: 'unpaid',
  remark: '',
  price_monthly: String(room.price_monthly),
  internet_fee: String(room.internet_fee),
  furniture_fee: String(room.furniture_fee),
  maintenance_fee: String(room.maintenance_fee),
  other_fee: String(room.other_fee),
  other_fee_label: room.other_fee_label ?? '',
  room_type: room.room_type,
  room_status: room.status,
  room_remark: room.remark ?? '',
  repair_details: room.repair_details ?? '',
  keycards: room.keycards?.join(', ') ?? '',
})

const recordToForm = (
  rec: Pick<
    MonthlyUtilityRow,
    | 'elec_unit_price'
    | 'elec_prev_reading'
    | 'elec_curr_reading'
    | 'water_unit_price'
    | 'water_prev_reading'
    | 'water_curr_reading'
    | 'record_date'
    | 'payment_status'
    | 'remark'
  >,
  room: Pick<
    MonthlyRoomRow,
    | 'room_type'
    | 'status'
    | 'price_monthly'
    | 'internet_fee'
    | 'furniture_fee'
    | 'maintenance_fee'
    | 'other_fee'
    | 'other_fee_label'
    | 'remark'
    | 'repair_details'
    | 'keycards'
  >,
): MonthlyEditableRowValues => ({
  elec_unit_price: String(rec.elec_unit_price),
  elec_prev_reading: String(rec.elec_prev_reading),
  elec_curr_reading: String(rec.elec_curr_reading),
  water_unit_price: String(rec.water_unit_price),
  water_prev_reading: String(rec.water_prev_reading),
  water_curr_reading: String(rec.water_curr_reading),
  record_date: rec.record_date,
  payment_status: rec.payment_status as 'paid' | 'unpaid',
  remark: rec.remark ?? '',
  price_monthly: String(room.price_monthly),
  internet_fee: String(room.internet_fee),
  furniture_fee: String(room.furniture_fee),
  maintenance_fee: String(room.maintenance_fee),
  other_fee: String(room.other_fee),
  other_fee_label: room.other_fee_label ?? '',
  room_type: room.room_type,
  room_status: room.status,
  room_remark: room.remark ?? '',
  repair_details: room.repair_details ?? '',
  keycards: room.keycards?.join(', ') ?? '',
})

const snapshotToForm = (
  snapshotRow: SnapshotDetailRow,
): MonthlyEditableRowValues => ({
  elec_unit_price: snapshotRow.elec_unit_price?.toString() ?? '',
  elec_prev_reading: snapshotRow.elec_prev_reading?.toString() ?? '',
  elec_curr_reading: snapshotRow.elec_curr_reading?.toString() ?? '',
  water_unit_price: snapshotRow.water_unit_price?.toString() ?? '',
  water_prev_reading: snapshotRow.water_prev_reading?.toString() ?? '',
  water_curr_reading: snapshotRow.water_curr_reading?.toString() ?? '',
  record_date: snapshotRow.record_date ?? '',
  payment_status:
    snapshotRow.payment_status === 'paid' ? 'paid' : ('unpaid' as const),
  remark: snapshotRow.remark ?? '',
  price_monthly: snapshotRow.price_monthly?.toString() ?? '',
  internet_fee: snapshotRow.internet_fee?.toString() ?? '',
  furniture_fee: snapshotRow.furniture_fee?.toString() ?? '',
  maintenance_fee: snapshotRow.maintenance_fee?.toString() ?? '',
  other_fee: snapshotRow.other_fee?.toString() ?? '',
  other_fee_label: snapshotRow.other_fee_label ?? '',
  room_type: snapshotRow.room_type ?? 'fan',
  room_status: snapshotRow.room_status ?? 'available',
  room_remark: snapshotRow.room_remark ?? '',
  repair_details: snapshotRow.repair_details ?? '',
  keycards: snapshotRow.keycards?.join(', ') ?? '',
})

const roomFromSnapshot = (snapshotRow: SnapshotDetailRow): MonthlyRoomRow =>
  ({
    room_number: snapshotRow.room_number,
    room_type: snapshotRow.room_type ?? 'fan',
    status: snapshotRow.room_status ?? 'available',
    price_monthly: snapshotRow.price_monthly,
    elec_unit_price: snapshotRow.elec_unit_price,
    water_unit_price: snapshotRow.water_unit_price,
    internet_fee: snapshotRow.internet_fee,
    furniture_fee: snapshotRow.furniture_fee,
    maintenance_fee: snapshotRow.maintenance_fee,
    other_fee: snapshotRow.other_fee,
    other_fee_label: snapshotRow.other_fee_label,
    keycards: snapshotRow.keycards ?? [],
    remark: snapshotRow.room_remark ?? null,
    repair_details: snapshotRow.repair_details ?? null,
    created_at: snapshotRow.created_at,
    updated_at: snapshotRow.created_at,
  }) as MonthlyRoomRow

const snapshotFieldValue = (
  snapshotRow: SnapshotDetailRow,
  field: SnapshotComparableField,
): unknown => {
  switch (field) {
    case 'room_status':
      return snapshotRow.room_status
    case 'room_remark':
      return snapshotRow.room_remark
    default:
      return snapshotRow[field]
  }
}

const missingSnapshotFields = (
  snapshotRow: SnapshotDetailRow,
): SnapshotComparableField[] =>
  SNAPSHOT_COMPARABLE_FIELDS.filter(
    (field) => snapshotFieldValue(snapshotRow, field) == null,
  )

const findContract = (
  contracts: MonthlyContractRow[],
  roomNumber: string,
): MonthlyContractRow | null =>
  contracts.find((contract) => contract.room_number === roomNumber) ?? null

const findExistingRecord = (
  existingRecords: MonthlyUtilityRow[],
  roomNumber: string,
  contract: MonthlyContractRow | null,
): MonthlyUtilityRow | null => {
  const recordByRoom =
    existingRecords.find((record) => record.room_number === roomNumber) ?? null

  if (!contract) return recordByRoom

  return (
    existingRecords.find(
      (record) =>
        record.room_number === roomNumber && record.contract_id === contract.id,
    ) ??
    recordByRoom ??
    null
  )
}

const toExistingRecord = (
  util: MonthlyUtilityRow | null,
  room: MonthlyRoomRow,
): MonthlyExistingUtilityRow | null => (util ? { util, room } : null)

const roomFieldToFormValue = (
  room: MonthlyRoomRow,
  field: RoomSyncField,
): string => {
  switch (field) {
    case 'price_monthly':
      return String(room.price_monthly)
    case 'internet_fee':
      return String(room.internet_fee)
    case 'furniture_fee':
      return String(room.furniture_fee)
    case 'maintenance_fee':
      return String(room.maintenance_fee)
    case 'other_fee':
      return String(room.other_fee)
    case 'other_fee_label':
      return room.other_fee_label ?? ''
    case 'room_type':
      return room.room_type
    case 'room_status':
      return room.status
    case 'room_remark':
      return room.remark ?? ''
    case 'repair_details':
      return room.repair_details ?? ''
    case 'keycards':
      return room.keycards?.join(', ') ?? ''
  }
}

export const applyRoomFieldsToRows = (
  rows: MonthlyRowEntry[],
  fields: RoomSyncField[],
): MonthlyRowEntry[] => {
  if (fields.length === 0) return rows

  return rows.map((row) => {
    const updates: Partial<MonthlyEditableRowValues> = {}

    for (const field of fields) {
      const nextValue = roomFieldToFormValue(row.room, field)
      if (row.form[field] !== nextValue) {
        updates[field] = nextValue
      }
    }

    if (Object.keys(updates).length === 0) {
      return row
    }

    return {
      ...row,
      form: { ...row.form, ...updates },
      dirty: true,
      errors: {},
    }
  })
}

const sourceFieldToFormValue = (
  row: MonthlyRowEntry,
  field: SnapshotComparableField,
): string | null => {
  switch (field) {
    case 'price_monthly':
    case 'internet_fee':
    case 'furniture_fee':
    case 'maintenance_fee':
    case 'other_fee':
    case 'other_fee_label':
    case 'room_type':
    case 'room_status':
    case 'room_remark':
    case 'repair_details':
    case 'keycards':
      return roomFieldToFormValue(row.room, field)
    case 'elec_unit_price':
      return row.existingRecord
        ? String(row.existingRecord.util.elec_unit_price)
        : null
    case 'elec_prev_reading':
      return row.existingRecord
        ? String(row.existingRecord.util.elec_prev_reading)
        : null
    case 'elec_curr_reading':
      return row.existingRecord
        ? String(row.existingRecord.util.elec_curr_reading)
        : null
    case 'water_unit_price':
      return row.existingRecord
        ? String(row.existingRecord.util.water_unit_price)
        : null
    case 'water_prev_reading':
      return row.existingRecord
        ? String(row.existingRecord.util.water_prev_reading)
        : null
    case 'water_curr_reading':
      return row.existingRecord
        ? String(row.existingRecord.util.water_curr_reading)
        : null
    case 'record_date':
      return row.existingRecord?.util.record_date ?? null
    case 'payment_status':
      return row.existingRecord?.util.payment_status ?? null
    case 'remark':
      return row.existingRecord?.util.remark ?? ''
  }
}

const assignSourceUpdate = (
  updates: Partial<MonthlyEditableRowValues>,
  field: SnapshotComparableField,
  value: string,
) => {
  if (field === 'payment_status') {
    if (value === 'paid' || value === 'unpaid') {
      updates.payment_status = value
    }
    return
  }

  const stringField = field as StringSnapshotComparableField
  updates[stringField] = value
}

export const applySourceFieldsToRows = (
  rows: MonthlyRowEntry[],
  fieldsByRoom: Record<string, SnapshotComparableField[]>,
): MonthlyRowEntry[] =>
  rows.map((row) => {
    const fields = fieldsByRoom[row.room.room_number] ?? []
    if (fields.length === 0) return row

    const updates: Partial<MonthlyEditableRowValues> = {}
    for (const field of fields) {
      const nextValue = sourceFieldToFormValue(row, field)
      if (nextValue !== null && row.form[field] !== nextValue) {
        assignSourceUpdate(updates, field, nextValue)
      }
    }

    if (Object.keys(updates).length === 0) return row

    return {
      ...row,
      form: { ...row.form, ...updates },
      dirty: true,
      errors: {},
    }
  })

export const buildLiveMonthlyRows = ({
  month,
  rooms,
  contracts,
  existingRecords,
  prevMonthRecords,
}: LiveMonthlyRowsInput): MonthlyRowEntry[] => {
  const recordDate = `${month}-01`
  const prevByRoom = new Map(
    prevMonthRecords.map((record) => [record.room_number, record]),
  )

  return rooms.map((room) => {
    const contract = findContract(contracts, room.room_number)
    const existing = findExistingRecord(
      existingRecords,
      room.room_number,
      contract,
    )
    const prevMonthRecord = prevByRoom.get(room.room_number)
    const form = existing
      ? recordToForm(existing, room)
      : emptyForm(room, recordDate)

    if (!existing && prevMonthRecord) {
      form.elec_prev_reading = String(prevMonthRecord.elec_curr_reading)
      form.water_prev_reading = String(prevMonthRecord.water_curr_reading)
    }

    return {
      source: 'live',
      tenantName: tenantNameFromContract(contract),
      room,
      contract,
      existingRecord: toExistingRecord(existing, room),
      form,
      initialForm: { ...form },
      errors: {},
      dirty: false,
    }
  })
}

export const buildSnapshotMonthlyRows = ({
  snapshotRows,
  rooms,
  contracts,
  existingRecords,
}: SnapshotMonthlyRowsInput): MonthlyRowEntry[] => {
  const roomByNumber = new Map(rooms.map((room) => [room.room_number, room]))

  return snapshotRows.map((snapshotRow) => {
    const room =
      roomByNumber.get(snapshotRow.room_number) ?? roomFromSnapshot(snapshotRow)
    const contract = findContract(contracts, snapshotRow.room_number)
    const existing = findExistingRecord(
      existingRecords,
      snapshotRow.room_number,
      contract,
    )

    const form = snapshotToForm(snapshotRow)

    return {
      source: 'snapshot',
      tenantName: snapshotRow.tenant_name,
      room,
      contract,
      existingRecord: toExistingRecord(existing, room),
      form,
      initialForm: { ...form },
      snapshotRow,
      snapshotForm: form,
      missingSnapshotFields: missingSnapshotFields(snapshotRow),
      errors: {},
      dirty: false,
    }
  })
}
